/**
 * Beyond Sure — Indian tool error boundary (isolation rule #6: error containment).
 *
 * Wraps the experimental tool so that ANY render error inside it is caught and shown
 * as a contained fallback — the app shell and every other tab keep working. This is
 * what guarantees a failing experiment can never take down the core product.
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  failed: boolean;
}

export class IndianErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Contained on purpose — do not rethrow. A real deployment would log to its own
    // experimental-tool telemetry, never the core error stream.
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          role="alert"
          data-testid="indian-error-boundary"
          className="m-6 rounded-2xl border border-[var(--color-paper-60)] bg-[var(--color-paper)] p-6 text-[var(--color-ink)]"
        >
          <p className="text-sm font-semibold">The Indian tool hit an error.</p>
          <p className="mt-1 text-sm text-[var(--color-ink-40)]">
            It is fully contained — the rest of Beyond Sure is unaffected. Reload to retry.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
